import React, { useEffect, useMemo, useState } from "react";
import Input from "../../components/ui/input.tsx";
import Label from "../../components/ui/label.tsx";
import Button from "../../components/ui/button.tsx";
import Checkbox from "../../components/ui/checkbox.tsx";
import { SimpleSelect } from "../../components/ui/select-shadcn.tsx";
import { useCurrencyList } from "../../hooks/useCurrencyList.tsx";

type TabKey = "profile" | "preferences" | "notifications" | "email";

const Settings: React.FC = () => {
  const [active, setActive] = useState<TabKey>("profile");

  // Profile state
  const userRaw =
    typeof window !== "undefined" ? localStorage.getItem("user") : null;
  const user = useMemo(() => (userRaw ? JSON.parse(userRaw) : {}), [userRaw]);
  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");
  const [userName, setUserName] = useState(user?.user_name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSaved, setProfileSaved] = useState<string | null>(null);

  useEffect(() => {
    setFirstName(user?.first_name || "");
    setLastName(user?.last_name || "");
    setUserName(user?.user_name || "");
    setEmail(user?.email || "");
  }, [userRaw, user]);

  const saveProfile = () => {
    setSavingProfile(true);
    setProfileSaved(null);
    try {
      const next = {
        ...user,
        first_name: firstName,
        last_name: lastName,
        user_name: userName,
        email,
      };
      localStorage.setItem("user", JSON.stringify(next));
      setProfileSaved("Saved");
    } catch {
      setProfileSaved("Failed");
    } finally {
      setSavingProfile(false);
      setTimeout(() => setProfileSaved(null), 2000);
    }
  };

  // Preferences state
  const { currencies } = useCurrencyList();
  const currencyOptions = useMemo(
    () =>
      currencies.map((c) => ({
        label: `${c.code} – ${c.name}`,
        value: c.code,
      })),
    [currencies]
  );
  const [baseCurrency, setBaseCurrency] = useState(
    (typeof window !== "undefined" &&
      localStorage.getItem("pref:baseCurrency")) ||
      "AED"
  );
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timezoneOptions = [
    { label: `System (${tz})`, value: tz || "system" },
    { label: "UTC", value: "UTC" },
  ];
  const [timezone, setTimezone] = useState(
    (typeof window !== "undefined" && localStorage.getItem("pref:timezone")) ||
      tz ||
      "UTC"
  );
  const themeOptions = [
    { label: "System", value: "system" },
    { label: "Dark", value: "dark" },
    { label: "Light", value: "light" },
  ];
  const [theme, setTheme] = useState(
    (typeof window !== "undefined" && localStorage.getItem("pref:theme")) ||
      "system"
  );
  const [prefsSaved, setPrefsSaved] = useState<string | null>(null);
  const savePrefs = () => {
    try {
      localStorage.setItem("pref:baseCurrency", baseCurrency);
      localStorage.setItem("pref:timezone", timezone);
      localStorage.setItem("pref:theme", theme);
      setPrefsSaved("Saved");
      setTimeout(() => setPrefsSaved(null), 2000);
    } catch {
      setPrefsSaved("Failed");
    }
  };

  // Notifications state
  const [notifTxn, setNotifTxn] = useState<boolean>(
    () => localStorage.getItem("notif:transactions") === "true"
  );
  const [notifSubs, setNotifSubs] = useState<boolean>(
    () => localStorage.getItem("notif:subscriptions") === "true"
  );
  const [notifRem, setNotifRem] = useState<boolean>(
    () => localStorage.getItem("notif:reminders") === "true"
  );
  const saveNotifs = () => {
    localStorage.setItem("notif:transactions", String(notifTxn));
    localStorage.setItem("notif:subscriptions", String(notifSubs));
    localStorage.setItem("notif:reminders", String(notifRem));
  };

  // Email state (basic example)
  const [altEmail, setAltEmail] = useState<string>(
    () => localStorage.getItem("email:alt") || ""
  );
  const [emailSaved, setEmailSaved] = useState<string | null>(null);
  const saveEmail = () => {
    try {
      localStorage.setItem("email:alt", altEmail.trim());
      setEmailSaved("Saved");
      setTimeout(() => setEmailSaved(null), 2000);
    } catch {
      setEmailSaved("Failed");
    }
  };

  const TabButton = ({ id, label }: { id: TabKey; label: string }) => (
    <button
      onClick={() => setActive(id)}
      className={[
        "btn-ghost px-3 py-1",
        active === id ? "ring-1 ring-indigo-400/50" : "",
      ].join(" ")}
      aria-current={active === id ? "page" : undefined}
    >
      {label}
    </button>
  );

  return (
    <section className="p-5 flex justify-center">
      <div className="container-site">
        <div className="flex flex-wrap gap-5 justify-between items-center mb-4">
          <div>
            <h3 className="heading-2">Settings</h3>
            <p className="sub-heading-2">
              Manage your account and app preferences.
            </p>
          </div>
        </div>

        {/* Top Tabs (Transactions-style) */}
        <div
          className="flex items-center gap-2 mb-4"
          role="tablist"
          aria-label="Settings tabs"
        >
          <TabButton id="profile" label="Profile" />
          <TabButton id="preferences" label="Preferences" />
          <TabButton id="notifications" label="Notifications" />
          <TabButton id="email" label="Email" />
        </div>

        {/* Content */}
        <div className="space-y-4">
          {active === "profile" && (
            <div className="card-glass p-4">
              <div className="widget-header pb-2">
                <div className="widget-title">Profile</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-1 block">First name</Label>
                  <Input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="mb-1 block">Last name</Label>
                  <Input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="mb-1 block">Username</Label>
                  <Input
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="mb-1 block">Email</Label>
                  <Input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <Button onClick={saveProfile} disabled={savingProfile}>
                  {savingProfile ? "Saving..." : "Save"}
                </Button>
                {profileSaved && (
                  <span className="text-sm text-white/70">{profileSaved}</span>
                )}
              </div>
            </div>
          )}

          {active === "preferences" && (
            <div className="card-glass p-4">
              <div className="widget-header pb-2">
                <div className="widget-title">Preferences</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-1 block">Base currency</Label>
                  <SimpleSelect
                    value={baseCurrency}
                    onValueChange={setBaseCurrency}
                    options={currencyOptions}
                  />
                </div>
                <div>
                  <Label className="mb-1 block">Timezone</Label>
                  <SimpleSelect
                    value={timezone}
                    onValueChange={setTimezone}
                    options={timezoneOptions}
                  />
                </div>
                <div>
                  <Label className="mb-1 block">Theme</Label>
                  <SimpleSelect
                    value={theme}
                    onValueChange={setTheme}
                    options={themeOptions}
                  />
                </div>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <Button onClick={savePrefs}>Save Preferences</Button>
                {prefsSaved && (
                  <span className="text-sm text-white/70">{prefsSaved}</span>
                )}
              </div>
            </div>
          )}

          {active === "notifications" && (
            <div className="card-glass p-4">
              <div className="widget-header pb-2">
                <div className="widget-title">Notifications</div>
              </div>
              <div className="space-y-3">
                <label className="flex items-center gap-3">
                  <Checkbox
                    checked={notifTxn}
                    onChange={(e) =>
                      setNotifTxn((e.target as HTMLInputElement).checked)
                    }
                  />
                  <span className="text-white/90">Transaction alerts</span>
                </label>
                <label className="flex items-center gap-3">
                  <Checkbox
                    checked={notifSubs}
                    onChange={(e) =>
                      setNotifSubs((e.target as HTMLInputElement).checked)
                    }
                  />
                  <span className="text-white/90">Subscription renewals</span>
                </label>
                <label className="flex items-center gap-3">
                  <Checkbox
                    checked={notifRem}
                    onChange={(e) =>
                      setNotifRem((e.target as HTMLInputElement).checked)
                    }
                  />
                  <span className="text-white/90">Reminders</span>
                </label>
              </div>
              <div className="mt-4">
                <Button onClick={saveNotifs}>Save Notification Settings</Button>
              </div>
            </div>
          )}

          {active === "email" && (
            <div className="card-glass p-4">
              <div className="widget-header pb-2">
                <div className="widget-title">Email</div>
              </div>
              <p className="text-white/70 text-sm mb-3">
                Manage your contact email addresses.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-1 block">Primary email</Label>
                  <Input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="mb-1 block">Alternate email</Label>
                  <Input
                    value={altEmail}
                    onChange={(e) => setAltEmail(e.target.value)}
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <Button
                  onClick={() => {
                    saveProfile();
                    saveEmail();
                  }}
                >
                  Save Emails
                </Button>
                {emailSaved && (
                  <span className="text-sm text-white/70">{emailSaved}</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Settings;
